import '../styles/FlashCards.css';

import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  collection,
  getDocs,
  getFirestore,
  query,
} from 'firebase/firestore';
import {
  useNavigate,
  useParams,
} from 'react-router-dom';

import firebase from '../firebase';
import { Athlete } from '../types/types';
import { checkSessionCodeAndLogoutIfChanged } from '../utils/sessionUtils';
import {
  Button,
  Card,
  Container,
  Typography,
} from './common';

interface FlashCardQuestion {
  athlete: Athlete;
  choices: string[];
  correctAnswer: string;
}

export default function FlashCards() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [filteredAthletes, setFilteredAthletes] = useState<Athlete[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentQuestion, setCurrentQuestion] = useState<FlashCardQuestion | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [gameStarted, setGameStarted] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  const [usedAthletes, setUsedAthletes] = useState<Set<string>>(new Set());
  const [isImageEnlarged, setIsImageEnlarged] = useState(false);
  
  // Filter states
  const [teamFilters, setTeamFilters] = useState<string[]>([]);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  
  const navigate = useNavigate();
  const db = getFirestore(firebase);

  useEffect(() => {
    checkSessionCodeAndLogoutIfChanged(localStorage.getItem('sessionId')!);

    async function fetchAthletes() {
      if (!sessionId) {
        setError('Session ID is missing');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');

        // Fetch athletes for this session
        const athletesQuery = query(collection(db, `sessions/${sessionId}/athletes`));
        const athletesSnapshot = await getDocs(athletesQuery);

        if (athletesSnapshot.empty) {
          setAthletes([]);
          setFilteredAthletes([]);
        } else {
          const athletesData = athletesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })) as Athlete[];

          // Filter out athletes with "no" team placement by default
          const validAthletes = athletesData.filter((a) => a.teamplacement != "no");

          // Store all valid athletes (we'll apply filters when starting the game)
          setAthletes(validAthletes);
          setFilteredAthletes(validAthletes);
        }
      } catch (error) {
        console.error('Error fetching athletes:', error);
        setError('Failed to load athletes');
      } finally {
        setLoading(false);
      }
    }

    fetchAthletes();
  }, [sessionId, db]);

const shuffle = (array: Athlete[]) => { 
    return array.map((a) => ({ sort: Math.random(), value: a }))
        .sort((a, b) => a.sort - b.sort)
        .map((a) => a.value); 
}; 

  const generateQuestion = useCallback((): FlashCardQuestion | null => {
    if (filteredAthletes.length < 4) {
      return null; // Need at least 4 athletes to generate choices
    }

    // Get available athletes (not used yet)
    const availableAthletes = filteredAthletes.filter(athlete => !usedAthletes.has(athlete.id));
    
    if (availableAthletes.length === 0) {
      return null; // No more athletes available
    }

    // Pick a random athlete
    const randomIndex = Math.floor(Math.random() * availableAthletes.length);
    const correctAthlete = availableAthletes[randomIndex];

    // Generate 3 wrong choices from other athletes
    const wrongChoices = filteredAthletes
      .filter(athlete => athlete.id !== correctAthlete.id)
      .map(athlete => athlete.name)
      .sort(() => Math.random() - 0.5)
      .slice(0, 3);

    // Combine correct answer with wrong choices and shuffle
    const allChoices = [correctAthlete.name, ...wrongChoices].sort(() => Math.random() - 0.5);

    return {
      athlete: correctAthlete,
      choices: allChoices,
      correctAnswer: correctAthlete.name
    };
  }, [filteredAthletes, usedAthletes]);

  const generateNextQuestion = useCallback(() => {
    const question = generateQuestion();
    if (question) {
      setCurrentQuestion(question);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      setGameEnded(true);
    }
  }, [generateQuestion]);

  // Start game when gameStarted is set to true (after filter selection)
  useEffect(() => {
    if (filteredAthletes.length >= 4 && gameStarted && !currentQuestion && !gameEnded) {
      generateNextQuestion();
    }
  }, [filteredAthletes, gameStarted, currentQuestion, gameEnded, generateNextQuestion]);

  const handleAnswerSelect = (answer: string) => {
    if (showResult || !currentQuestion) return;
    
    setSelectedAnswer(answer);
    setShowResult(true);
    
    const isCorrect = answer === currentQuestion.correctAnswer;
    setScore(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1
    }));

    // Mark this athlete as used
    setUsedAthletes(prev => new Set(Array.from(prev).concat(currentQuestion.athlete.id)));
  };

  const handleNext = () => {
    generateNextQuestion();
  };

  const handleImageClick = () => {
    setIsImageEnlarged(true);
  };

  const handleCloseEnlarged = () => {
    setIsImageEnlarged(false);
  };

  const handleRestart = () => {
    setGameEnded(false);
    setGameStarted(false);
    setCurrentQuestion(null);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore({ correct: 0, total: 0 });
    setUsedAthletes(new Set());
  };
  
  // Toggle team placement filter
  const toggleTeamFilter = (placement: string) => {
    setTeamFilters(prev => {
      if (prev.includes(placement)) {
        return prev.filter(p => p !== placement);
      } else {
        return [...prev, placement];
      }
    });
  };
  
  // Toggle returning status filter
  const toggleStatusFilter = (status: string) => {
    setStatusFilters(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };
  
  // Apply filters and start the game
  const startGame = () => {
    let filtered = [...athletes];
    
    // Apply team placement filters if any are selected
    if (teamFilters.length > 0) {
      filtered = filtered.filter(athlete => teamFilters.includes(athlete.teamplacement || ''));
    }
    
    // Apply returning status filters if any are selected
    if (statusFilters.length > 0) {
      filtered = filtered.filter(athlete => statusFilters.includes(athlete.returningstatus || ''));
    }
    
    // Shuffle the filtered athletes
    const shuffledAthletes = shuffle(filtered);
    setFilteredAthletes(shuffledAthletes);
    
    // Reset game state
    setUsedAthletes(new Set());
    setScore({ correct: 0, total: 0 });
    
    // Start the game
    setGameStarted(true);
  };

  const getTeamPlacementInfo = (teamplacement?: string) => {
    if (teamplacement === 'coed') {
      return { label: 'COED', color: 'green' };
    } else if (teamplacement === 'allgirl') {
      return { label: 'ALLGIRL', color: 'green' };
    } else if (teamplacement === 'level4') {
      return { label: 'LVL4', color: 'green' };
    } 
    return { label: 'ERROR', color: 'gray' };
  };

  if (!sessionId) {
    return (
      <Container>
        <Card>
          <Typography variant="h2" align="center">Error</Typography>
          <div className="error-message">Session ID is missing</div>
          <Button
            variant="primary"
            fullWidth
            onClick={() => navigate('/join')}
          >
            Join a Session
          </Button>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container>
        <div className="new-athlete-top-bar">
          <div className="new-athlete-top-bar-left">
            <button className="new-athlete-top-bar-button" onClick={() => navigate(`/session/${sessionId}`)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor" />
              </svg>
            </button>
          </div>
          <div className="new-athlete-top-bar-right"></div>
        </div>
        <div className="flash-cards-content">
          <Card>
            <Typography variant="h2">Flash Cards</Typography>
            <div className="loading-content">
              <div className="shimmer shimmer-text long"></div>
              <div className="shimmer shimmer-text medium"></div>
              <div className="shimmer shimmer-text short"></div>
            </div>
          </Card>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div className="new-athlete-top-bar">
          <div className="new-athlete-top-bar-left">
            <button className="new-athlete-top-bar-button" onClick={() => navigate(`/session/${sessionId}`)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor" />
              </svg>
            </button>
          </div>
          <div className="new-athlete-top-bar-right"></div>
        </div>
        <div className="flash-cards-content">
          <Card>
            <Typography variant="h2" align="center">Error</Typography>
            <div className="error-message">{error}</div>
            <Button
              variant="primary"
              fullWidth
              onClick={() => navigate(`/session/${sessionId}`)}
            >
              Back
            </Button>
          </Card>
        </div>
      </Container>
    );
  }

  if (athletes.length < 4) {
    return (
      <Container>
        <div className="new-athlete-top-bar">
          <div className="new-athlete-top-bar-left">
            <button className="new-athlete-top-bar-button" onClick={() => navigate(`/session/${sessionId}`)}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor" />
              </svg>
            </button>
          </div>
          <div className="new-athlete-top-bar-right"></div>
        </div>
        <div className="flash-cards-content">
          <Card>
            <Typography variant="h2">Flash Cards</Typography>
            <div className="no-athletes">
              <Typography variant="h3">Not Enough Athletes</Typography>
              <Typography>You need at least 4 athletes to play flash cards.</Typography>
              <Button
                variant="primary"
                fullWidth
                onClick={() => navigate(`/session/${sessionId}/add-athlete`)}
              >
                Add Athletes
              </Button>
            </div>
          </Card>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="new-athlete-top-bar">
        <div className="new-athlete-top-bar-left">
          <button className="new-athlete-top-bar-button" onClick={() => navigate(`/session/${sessionId}`)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" fill="currentColor" />
            </svg>
          </button>
        </div>
        <div className="new-athlete-top-bar-center">
          <span>Flash Cards</span>
        </div>
        <div className="new-athlete-top-bar-right"></div>
      </div>
      
      <Card className='flash-cards-card'>
        <Typography variant="h2">Flash Cards</Typography>
        
        {/* Filter Selection UI */}
        {!gameStarted && !gameEnded && (
          <div className="filter-section">
            <Typography variant="h3">Select Filters</Typography>
            <Typography variant="body">Filter athletes by team placement and status</Typography>
            
            <div className="filter-group">
              <Typography variant="h4">Team Placement</Typography>
              <div className="filter-options">
                {['coed', 'allgirl', 'level4'].map(placement => {
                  const { label, color } = getTeamPlacementInfo(placement);
                  const isSelected = teamFilters.includes(placement);
                  
                  return (
                    <button 
                      key={placement}
                      className={`filter-chip ${isSelected ? 'selected' : ''}`}
                      style={{ borderColor: isSelected ? color : undefined }}
                      onClick={() => toggleTeamFilter(placement)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div className="filter-group">
              <Typography variant="h4">Returning Status</Typography>
              <div className="filter-options">
                {["New", "Returning", "Summer", "Unknown"].map(status => {
                  const isSelected = statusFilters.includes(status);
                  
                  return (
                    <button 
                      key={status}
                      className={`filter-chip ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleStatusFilter(status)}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div className="filter-summary">
              <Typography variant="body">
                {teamFilters.length === 0 && statusFilters.length === 0 ? 
                  'All eligible athletes will be included' : 
                  `${athletes.filter(a => {
                    // If no team filters, all team placements pass
                    const teamPasses = teamFilters.length === 0 || teamFilters.includes(a.teamplacement || '');
                    // If no status filters, all statuses pass
                    const statusPasses = statusFilters.length === 0 || statusFilters.includes(a.returningstatus || '');
                    return teamPasses && statusPasses;
                  }).length} athletes match your filters`
                }
              </Typography>
            </div>
            
            {/* Show warning if filters don't leave enough athletes */}
            {athletes.filter(a => {
              const teamPasses = teamFilters.length === 0 || teamFilters.includes(a.teamplacement || '');
              const statusPasses = statusFilters.length === 0 || statusFilters.includes(a.returningstatus || '');
              return teamPasses && statusPasses;
            }).length < 4 ? (
              <div className="filter-warning">
                <Typography variant="body">
                  Not enough athletes match your filters. You need at least 4 athletes to play.
                </Typography>
              </div>
            ) : null}
            
            <Button
              variant="primary"
              fullWidth
              onClick={startGame}
              disabled={athletes.filter(a => {
                const teamPasses = teamFilters.length === 0 || teamFilters.includes(a.teamplacement || '');
                const statusPasses = statusFilters.length === 0 || statusFilters.includes(a.returningstatus || '');
                return teamPasses && statusPasses;
              }).length < 4}
            >
              Start Game
            </Button>
          </div>
        )}

        {gameStarted && currentQuestion && !gameEnded && (
          <div className="question-container">
            <div className="score-display">
              <Typography variant="body">
                Score: {score.correct}/{score.total} | Remaining: {filteredAthletes.length - usedAthletes.size}
              </Typography>
              
              {/* Show active filters during game */}
              {(teamFilters.length > 0 || statusFilters.length > 0) && (
                <div className="active-game-filters">
                  {teamFilters.length > 0 && (
                    <div className="game-filter-tag">
                      <span className="game-filter-label">Teams:</span>
                      {teamFilters.map(filter => (
                        <span key={filter} className="game-filter-value">
                          {getTeamPlacementInfo(filter).label}
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {statusFilters.length > 0 && (
                    <div className="game-filter-tag">
                      <span className="game-filter-label">Status:</span>
                      {statusFilters.map(filter => (
                        <span key={filter} className="game-filter-value">
                          {filter.charAt(0).toUpperCase() + filter.slice(1)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="question-layout">
              <div className="athlete-section">
                <div className="athlete-image-container">
                  <div className="athlete-image" onClick={handleImageClick}>
                    {currentQuestion.athlete.imageUrl ? (
                      <img 
                        src={currentQuestion.athlete.imageUrl} 
                        alt="Athlete" 
                        loading="lazy"
                      />
                    ) : (
                      <div className="placeholder-image">
                        <span>{currentQuestion.athlete.name.charAt(0)}</span>
                      </div>
                    )}
                    
                    {showResult && (
                      <div className={`athlete-info-overlay ${selectedAnswer === currentQuestion.correctAnswer ? 'correct-overlay' : 'incorrect-overlay'}`}>
                        <div className="result-message">
                        </div>
                        <div className="athlete-info">
                          <Typography variant="h4" align='center'>{currentQuestion.athlete.name} {selectedAnswer === currentQuestion.correctAnswer ? "✅" : "❌"}</Typography>
                          <div className="athlete-details">
                            <div className="detail-item">
                            <Typography variant="h4" className="detail-value">{currentQuestion.athlete.returningstatus}</Typography>
                              <Typography variant="h4" className="detail-value">{currentQuestion.athlete.position}</Typography>
                              <div>
                                <Typography variant="h4" className="team-placement-text">
                                  {getTeamPlacementInfo(currentQuestion.athlete.teamplacement).label}
                                </Typography>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="choices-container">
                  {currentQuestion.choices.map((choice, index) => {
                    const isSelected = selectedAnswer === choice;
                    const isCorrect = choice === currentQuestion.correctAnswer;
                    let buttonClass = 'choice-button';
                    
                    if (showResult) {
                      if (isCorrect) {
                        buttonClass += ' correct';
                      } else if (isSelected && !isCorrect) {
                        buttonClass += ' incorrect';
                      }
                    } else if (isSelected) {
                      buttonClass += ' selected';
                    }

                    return (
                      <Button
                        key={index}
                        variant="secondary"
                        fullWidth
                        className={buttonClass}
                        onClick={() => handleAnswerSelect(choice)}
                        disabled={showResult}
                      >
                        {choice}
                      </Button>
                    );
                  })}
                </div>

                {showResult && (
                  <Button
                    variant="primary"
                    fullWidth
                    onClick={handleNext}
                    className="next-button"
                  >
                    Next Question
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {gameEnded && (
          <div className="game-end">
            <Typography variant="h3">Game Complete!</Typography>
            <Typography variant="h4">
              Final Score: {score.correct}/{score.total}
            </Typography>
            <Typography variant="body">
              Accuracy: {score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0}%
            </Typography>
            
            {/* Display active filters if any */}
            {(teamFilters.length > 0 || statusFilters.length > 0) && (
              <div className="active-filters-summary">
                {teamFilters.length > 0 && (
                  <div className="filter-tag">
                    <span className="filter-tag-label">Team Placements:</span>
                    {teamFilters.map(filter => (
                      <span key={filter} className="filter-tag-value">
                        {getTeamPlacementInfo(filter).label}
                      </span>
                    ))}
                  </div>
                )}
                
                {statusFilters.length > 0 && (
                  <div className="filter-tag">
                    <span className="filter-tag-label">Status:</span>
                    {statusFilters.map(filter => (
                      <span key={filter} className="filter-tag-value">
                        {filter.charAt(0).toUpperCase() + filter.slice(1)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <div className="game-end-actions">
              <Button
                variant="primary"
                fullWidth
                onClick={handleRestart}
              >
                Play Again
              </Button>
              <Button
                variant="secondary"
                fullWidth
                onClick={() => navigate(`/session/${sessionId}`)}
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Enlarged Image Modal */}
      {isImageEnlarged && currentQuestion && (
        <div className="enlarged-image-modal" onClick={handleCloseEnlarged}>
          <div className="enlarged-image-container" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={handleCloseEnlarged}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <div className="enlarged-image">
              {currentQuestion.athlete.imageUrl ? (
                <img 
                  src={currentQuestion.athlete.imageUrl} 
                  alt="Athlete" 
                  loading="lazy"
                />
              ) : (
                <div className="enlarged-placeholder-image">
                  <span>{currentQuestion.athlete.name.charAt(0)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}
